import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
  Pipe,
  PipeTransform,
  signal,
  ViewEncapsulation,
} from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { Hotkey, HotkeyData } from 'wflow-cdk';
import { IconsModule, WuiButtonModule } from 'wflow-ui';

enum HotkeyCategory {
  GENERAL = 'general',
  DOCUMENT = 'document',
  APPROVAL = 'approval',
}

type CategoryNavigationItem = {
  key: HotkeyCategory;
  icon: string;
  title: string;
};

@Pipe({
  name: 'getKeysFromHint',
})
export class GetKeysFromHintPipe implements PipeTransform {
  transform(hint: string): string[] {
    return hint.split(' + ').map((key) => key.trim());
  }
}

@Component({
  selector: 'wf-hotkeys-info',
  templateUrl: './hotkeys-info.component.html',
  styleUrls: ['./hotkeys-info.component.scss'],
  imports: [IconsModule, WuiButtonModule, GetKeysFromHintPipe, TranslateModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  host: {
    class: 'wf-hotkeys-info no-overflow',
  },
})
export class HotkeysInfoComponent {
  readonly registeredHotkeys = input<HotkeyData[]>([]);

  readonly registeredHotkeysByCategory = computed<
    Record<HotkeyCategory, HotkeyData[]>
  >(() => {
    const registeredHotkeys = this.registeredHotkeys();

    return {
      [HotkeyCategory.GENERAL]: [
        ...registeredHotkeys.filter((hotkey) =>
          hotkey.command.includes('GENERAL'),
        ),
      ],
      [HotkeyCategory.DOCUMENT]: [
        ...registeredHotkeys.filter(
          (hotkey) =>
            hotkey.command.includes('DOCUMENT_NAVIGATION') ||
            hotkey.command.includes('DOCUMENT_ACTIONS'),
        ),
      ],
      [HotkeyCategory.APPROVAL]: [
        ...registeredHotkeys.filter((hotkey) =>
          hotkey.command.includes('DOCUMENT_APPROVALS'),
        ),
      ],
    };
  });

  readonly categoryNavigationConfig = {
    [HotkeyCategory.GENERAL]: {
      key: HotkeyCategory.GENERAL,
      icon: 'wflow',
      title: 'HOTKEYS.CATEGORIES.GENERAL.Title',
      description: 'HOTKEYS.CATEGORIES.GENERAL.Description',
    },
    [HotkeyCategory.DOCUMENT]: {
      key: HotkeyCategory.DOCUMENT,
      icon: 'file-06',
      title: 'HOTKEYS.CATEGORIES.DOCUMENT.Title',
      description: 'HOTKEYS.CATEGORIES.DOCUMENT.Description',
    },
    [HotkeyCategory.APPROVAL]: {
      key: HotkeyCategory.APPROVAL,
      icon: 'check-circle-broken',
      title: 'HOTKEYS.CATEGORIES.APPROVAL.Title',
      description: 'HOTKEYS.CATEGORIES.APPROVAL.Description',
    },
  };

  readonly hotkeyCategory = HotkeyCategory;
  readonly activeCategory = signal<CategoryNavigationItem>(
    this.categoryNavigationConfig[HotkeyCategory.GENERAL],
  );

  readonly categoryNavigationArray = Object.values(
    this.categoryNavigationConfig,
  );

  trackByCategoryNavigationItem(
    _index: number,
    categoryNavigationItem: Hotkey,
  ) {
    return `${categoryNavigationItem.key}-${categoryNavigationItem.command}-${_index}`;
  }
}
